import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  Pagination,
  Paper,
  Stack,
  Typography,
  Box,
  Card,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  alpha,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableItem from "../../../components/TableItem";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import PriceHistoryDialog from "./PriceHistoryDialog";
import ManualPriceDialog from "./ManualPriceDialog";
import {
  getCurrentPriceList,
  getPriceHistory,
  getPriceStatistics,
} from "../../../services/ImportPrice";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const StatsCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.08)",
  height: "100%",
}));

// Custom TableItem wrapper that adds row highlighting
const HighlightedTableItem = styled(TableItem)(({ theme }) => ({
  "& .MuiTableRow-root": {
    transition: "all 0.2s ease",
  },
  "& .MuiTableRow-root:hover": {
    backgroundColor: alpha(theme.palette.action.hover, 0.04),
  },
  // Highlight rows that have the warning icon (no pricing)
  "& .MuiTableRow-root:has(.no-price-indicator)": {
    backgroundColor: alpha("#ff9800", 0.08),
    borderLeft: `4px solid ${alpha("#ff9800", 0.6)}`,
  },
  "& .MuiTableRow-root:has(.no-price-indicator):hover": {
    backgroundColor: alpha("#ff9800", 0.15),
  },
}));

function ImportPrices() {
  const history = useHistory();

  // State management
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [openManualPriceDialog, setOpenManualPriceDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [statistics, setStatistics] = useState(null);

  // Filter state
  const [filter, setFilter] = useState({
    itemName: "",
    productCode: "",
    brand: "",
    priceType: "",
    minPrice: "",
    maxPrice: "",
  });

  // Statistics state
  const [stats, setStats] = useState({
    totalItems: 0,
    withoutPrice: 0,
    avgPrice: 0,
    highestPrice: 0,
    lowestPrice: 0,
  });

  // Table columns configuration
  const columns = [
    {
      id: "indicator",
      label: "",
      minWidth: 30,
      format: (value) => value, // This will render the warning icon
    },
    { id: "code", label: "Product Code", minWidth: 100 },
    { id: "item", label: "Item Name", minWidth: 200 },
    { id: "brand", label: "Brand", minWidth: 120 },
    { id: "unitType", label: "Unit", minWidth: 80 },
    {
      id: "currentPrice",
      label: "Current Price",
      minWidth: 140,
    },
    {
      id: "priceType",
      label: "Price Type",
      minWidth: 140,
      format: (value) => (
        <Chip
          label={value || "N/A"}
          size="small"
          color={
            value === "IMPORT_BILL"
              ? "primary"
              : value === "SUB_ORDER"
                ? "secondary"
                : "default"
          }
          variant="outlined"
        />
      ),
    },
    {
      id: "lastUpdated",
      label: "Last Updated",
      minWidth: 160,
    },
    { id: "action", label: "Actions", minWidth: 120 },
  ];

  // Create data row factory function
  // KEY: "action" field holds the importId — this is what TableItem passes to handleAction
  function createData(
    code,
    item,
    brand,
    unitType,
    currentPrice,
    priceType,
    lastUpdated,
    updatedBy,
    importId,
    priceId,
  ) {
    const hasNoPrice = !currentPrice || currentPrice === 0;

    return {
      indicator: hasNoPrice ? (
        <Tooltip title="No price assigned">
          <WarningIcon
            className="no-price-indicator"
            sx={{ color: "#ff9800", fontSize: 20 }}
          />
        </Tooltip>
      ) : null,
      code,
      item,
      brand,
      unitType,
      currentPrice: currentPrice ? `Rs.${currentPrice.toFixed(2)}` : "N/A",
      priceType,
      lastUpdated: lastUpdated
        ? new Date(lastUpdated).toLocaleDateString() +
          " by " +
          (updatedBy || "N/A")
        : "N/A",
      action: importId, // TableItem reads row["action"] and passes it as the id
      importId, // keep raw importId for lookup
      priceId,
      updatedBy,
      rawPrice: currentPrice, // keep raw number for the edit dialog
      hasNoPrice, // flag for tracking
    };
  }

  // Initial data load
  useEffect(() => {
    fetchData();
  }, []);

  // Re-fetch on page or rowsPerPage change
  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getCurrentPriceList();

      if (result && result.length > 0) {
        let filteredData = result;

        if (filter.itemName) {
          filteredData = filteredData.filter((item) =>
            item.itemName
              ?.toLowerCase()
              .includes(filter.itemName.toLowerCase()),
          );
        }
        if (filter.productCode) {
          filteredData = filteredData.filter((item) =>
            item.productCode
              ?.toLowerCase()
              .includes(filter.productCode.toLowerCase()),
          );
        }
        if (filter.brand) {
          filteredData = filteredData.filter((item) =>
            item.brand?.toLowerCase().includes(filter.brand.toLowerCase()),
          );
        }
        if (filter.priceType) {
          filteredData = filteredData.filter(
            (item) => item.priceType === filter.priceType,
          );
        }
        if (filter.minPrice) {
          filteredData = filteredData.filter(
            (item) => item.currentPrice >= parseFloat(filter.minPrice),
          );
        }
        if (filter.maxPrice) {
          filteredData = filteredData.filter(
            (item) => item.currentPrice <= parseFloat(filter.maxPrice),
          );
        }

        // Calculate statistics
        const prices = filteredData
          .map((item) => item.currentPrice)
          .filter((price) => price != null && price > 0);

        const withoutPrice = filteredData.filter(
          (item) => !item.currentPrice || item.currentPrice === 0,
        ).length;

        setStats({
          totalItems: filteredData.length,
          withoutPrice: withoutPrice,
          avgPrice:
            prices.length > 0
              ? prices.reduce((a, b) => a + b, 0) / prices.length
              : 0,
          highestPrice: prices.length > 0 ? Math.max(...prices) : 0,
          lowestPrice: prices.length > 0 ? Math.min(...prices) : 0,
        });

        // Pagination
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        setTotalRows(filteredData.length);

        // Transform to table rows
        const newSet = paginatedData.map((item) =>
          createData(
            item.productCode,
            item.itemName,
            item.brand,
            item.unitType,
            item.currentPrice,
            item.priceType,
            item.lastUpdatedDate,
            item.updatedBy,
            item.importId,
            item.priceId,
          ),
        );

        setRows(newSet);
      } else {
        setRows([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching price data:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
  };

  // Action handler
  // TableItem calls: handleAction("view", row["action"])
  // row["action"] = importId, so we find the full row from rows state
  const handleAction = async (event, importId) => {
    // Find the matching row from current rows state using importId
    const matchedRow = rows.find((r) => r.importId === importId);

    switch (event) {
      case "view":
        await viewPriceHistory(importId, matchedRow);
        break;
      case "edit":
        openEditPriceDialog(importId, matchedRow);
        break;
      default:
        return;
    }
  };

  // View price history dialog
  const viewPriceHistory = async (importId, row) => {
    try {
      setLoading(true);
      const historyData = await getPriceHistory(importId);
      const statsData = await getPriceStatistics(importId);

      setSelectedItem({
        importId: importId,
        itemName: row?.item,
        productCode: row?.code,
        brand: row?.brand,
      });
      setPriceHistory(historyData || []);
      setStatistics(statsData);
      setOpenHistoryDialog(true);
    } catch (error) {
      console.error("Error fetching price history:", error);
    } finally {
      setLoading(false);
    }
  };

  // Open edit/manual price dialog
  const openEditPriceDialog = (importId, row) => {
    setSelectedItem({
      importId: importId,
      itemName: row?.item,
      productCode: row?.code,
      currentPrice: row?.rawPrice, // use the raw number, not the formatted string
    });
    setOpenManualPriceDialog(true);
  };

  // Reset filters
  const resetFilters = () => {
    setFilter({
      itemName: "",
      productCode: "",
      brand: "",
      priceType: "",
      minPrice: "",
      maxPrice: "",
    });
    setPage(0);
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const totalPages = Math.ceil(totalRows / rowsPerPage);

  return (
    <>
      <StyledPaper elevation={0}>
        <HeaderCard>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <AttachMoneyIcon fontSize="large" />
            </Grid>
            <Grid item xs>
              <Typography variant="h4" fontWeight="bold">
                Material Prices
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Track and manage pricing for all import materials
              </Typography>
            </Grid>
          </Grid>
        </HeaderCard>

        {/* Statistics Cards */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <Typography variant="caption" color="textSecondary">
                Total Items
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {stats.totalItems}
              </Typography>
            </StatsCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <Typography variant="caption" color="textSecondary">
                Without Price
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="warning">
                {stats.withoutPrice}
              </Typography>
            </StatsCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <Typography variant="caption" color="textSecondary">
                Average Price
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">
                Rs.{stats.avgPrice.toFixed(2)}
              </Typography>
            </StatsCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard>
              <Typography variant="caption" color="textSecondary">
                Highest Price
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="error">
                Rs.{stats.highestPrice.toFixed(2)}
              </Typography>
            </StatsCard>
          </Grid>
        </Grid>

        {/* Search and Filter Bar */}
        <Box
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <TextField
            placeholder="Search by item name or product code..."
            variant="outlined"
            size="small"
            sx={{ width: "40%" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              handleFilterChange("itemName", e.target.value);
              if (e.target.value.length > 2 || e.target.value.length === 0) {
                fetchData();
              }
            }}
          />
          <Stack direction="row" spacing={2}>
            <Tooltip title="Toggle Filters">
              <ActionButton
                variant={showFilters ? "contained" : "outlined"}
                color="primary"
                startIcon={<FilterListIcon />}
                onClick={toggleFilters}
              >
                Filters
              </ActionButton>
            </Tooltip>
            <Chip
              label={`${totalRows} Items`}
              variant="outlined"
              color="primary"
              icon={<InfoIcon />}
            />
          </Stack>
        </Box>

        {/* Advanced Filters */}
        {showFilters && (
          <FilterContainer>
            <Typography variant="subtitle2" fontWeight="bold" mb={2}>
              Advanced Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <InputField
                  name="productCode"
                  value={filter.productCode}
                  onChange={(event, newInputValue) =>
                    handleFilterChange(event, newInputValue)
                  }
                  type="text"
                  label="Product Code"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <InputField
                  name="brand"
                  value={filter.brand}
                  onChange={(event, newInputValue) =>
                    handleFilterChange(event, newInputValue)
                  }
                  type="text"
                  label="Brand"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <InputField
                  name="priceType"
                  value={filter.priceType}
                  onChange={(event, newInputValue) =>
                    handleFilterChange(event, newInputValue)
                  }
                  type="select"
                  label="Price Type"
                  fullWidth
                  options={[
                    { value: "", label: "All" },
                    { value: "IMPORT_BILL", label: "Import Bill" },
                    { value: "MANUALLY_DEFINED", label: "Manual" },
                    { value: "SUB_ORDER", label: "Sub Order" },
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <InputField
                  name="minPrice"
                  value={filter.minPrice}
                  onChange={(event, newInputValue) =>
                    handleFilterChange(event, newInputValue)
                  }
                  type="number"
                  label="Min Price"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <InputField
                  name="maxPrice"
                  value={filter.maxPrice}
                  onChange={(event, newInputValue) =>
                    handleFilterChange(event, newInputValue)
                  }
                  type="number"
                  label="Max Price"
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Stack direction="row" spacing={2}>
                <Tooltip title="Reset Filters">
                  <ActionButton
                    variant="outlined"
                    color="secondary"
                    startIcon={<RestartAltIcon />}
                    onClick={() => {
                      resetFilters();
                      fetchData();
                    }}
                  >
                    Reset
                  </ActionButton>
                </Tooltip>
                <Tooltip title="Apply Filters">
                  <ActionButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setPage(0);
                      fetchData();
                    }}
                  >
                    Apply Filters
                  </ActionButton>
                </Tooltip>
              </Stack>
            </Box>
          </FilterContainer>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Info Box for Color Coding */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: alpha("#ff9800", 0.1),
            border: `1px solid ${alpha("#ff9800", 0.3)}`,
          }}
        >
          <WarningIcon sx={{ color: "#ff9800" }} />
          <Typography variant="body2" color="text.secondary">
            <strong>Rows with warning icon</strong> indicate materials without
            assigned prices. Click "Update Price" to set pricing.
          </Typography>
        </Box>

        {/* Table Content */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 5 }}>
            <Typography variant="body1" color="textSecondary">
              No price data found. Try adjusting your filters.
            </Typography>
          </Box>
        ) : (
          <HighlightedTableItem
            columns={columns}
            rows={rows}
            page={page}
            setPage={setPage}
            tablePagin={false}
            totalPages={totalPages}
            handleAction={handleAction}
            showActions={{
              view: "View Price History",
              edit: "Update Price",
            }}
            actionRoles={{
              view: [
                "INVENTORY_PRICE_VIEW",
                "INVENTORY_PRICE_CREATE",
                "INVENTORY_PRICE_UPDATE",
                "ROLE_ADMIN",
              ],
              edit: [
                "INVENTORY_PRICE_VIEW",
                "INVENTORY_PRICE_CREATE",
                "INVENTORY_PRICE_UPDATE",
                "ROLE_ADMIN",
              ],
            }}
          />
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationContainer>
            <Pagination
              count={totalPages}
              page={page + 1}
              variant="outlined"
              shape="rounded"
              color="primary"
              onChange={handleChange}
              size="medium"
            />
          </PaginationContainer>
        )}
      </StyledPaper>

      {/* Price History Dialog */}
      {openHistoryDialog && (
        <PriceHistoryDialog
          open={openHistoryDialog}
          onClose={() => setOpenHistoryDialog(false)}
          selectedItem={selectedItem}
          priceHistory={priceHistory}
          statistics={statistics}
        />
      )}

      {/* Manual Price Update Dialog */}
      {openManualPriceDialog && (
        <ManualPriceDialog
          open={openManualPriceDialog}
          onClose={() => setOpenManualPriceDialog(false)}
          selectedItem={selectedItem}
          onSuccess={() => {
            setOpenManualPriceDialog(false);
            fetchData();
          }}
        />
      )}
    </>
  );
}

export default ImportPrices;
